import * as React from 'react';
import { css } from '@emotion/react';
import { Box, Stack, Text, Draft } from '@resi-media/resi-ui';
import { useLocalStorage } from 'usehooks-ts';
import { useMockData } from '@preview/contexts/MockDataContext';
import { useRefresh } from '@preview/contexts/RefreshContext';
import type { VimeoAlbum, VimeoUser, VimeoVideo } from '@preview/types/vimeo';

type VimeoAggAlbums = {
  data: VimeoAlbum.Datum[];
  total: number;
};

type VimeoAggVideos = { [key: string]: VimeoVideo.Response };

type VimeoData = {
  albumsData: VimeoAggAlbums;
  userData?: VimeoUser.Response;
  videosData: VimeoAggVideos;
};

const VimeoDataFetcherCard = () => {
  const API_TOKEN = '0aac7c878453f61e44578706ec5b2250';
  const [userId, setUserId] = useLocalStorage('preview-vimeo-user-id', '');
  const [numAlbums, setNumAlbums] = useLocalStorage('preview-vimeo-num-albums', 2);
  const [numVideos, setNumVideos] = useLocalStorage('preview-vimeo-num-videos', 2);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const { addPlaylistMock, addSiteMock, addVideoMock, clearAllMocks, embedId, resetPendingChanges } = useMockData();
  const { triggerRefresh } = useRefresh();

  const fetchVimeoData = async () => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // We'll clear all mocks after successful fetch
    try {
      const albumsDataAgg: VimeoAggAlbums = { data: [], total: 0 };
      const videosDataAgg: VimeoAggVideos = {};
      const headers = {
        Authorization: `bearer ${API_TOKEN}`,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.vimeo.*+json;version=3.4',
      };

      // 1. Fetch user data
      const userResponse = await fetch(`https://api.vimeo.com/users/${userId}?fields=name,bio,link`, { headers });

      if (!userResponse.ok) {
        throw new Error(`User API error: ${userResponse.status}`);
      }

      const userData: VimeoUser.Response = await userResponse.json();

      // Recursive function to fetch albums until we have enough with videos
      const fetchAlbumsWithVideos = async (page = 1, albumsWithVideos = 0): Promise<number> => {
        // If we've found enough albums with videos, return
        if (albumsWithVideos >= numAlbums) {
          return albumsWithVideos;
        }

        // Fetch a page of albums
        const albumsResponse = await fetch(
          `https://api.vimeo.com/users/${userId}/albums?per_page=5&page=${page}&filter=public&sort=date&direction=desc&fields=uri,name,metadata.connections.videos.total`,
          { headers }
        );

        if (!albumsResponse.ok) {
          throw new Error(`Albums API error: ${albumsResponse.status}`);
        }

        const albumsData: VimeoAlbum.Response = await albumsResponse.json();

        // If no more albums are available, return what we have
        if (!albumsData.data || albumsData.data.length === 0) {
          return albumsWithVideos;
        }

        // Process each album in this page
        for (const album of albumsData.data) {
          // Stop if we've reached the requested number of albums with videos
          if (albumsWithVideos >= numAlbums) {
            return albumsWithVideos;
          }

          // Check if album has videos before fetching them
          const videoCount = album.metadata.connections.videos.total || 0;

          if (videoCount === 0) {
            // Skip albums with no videos
            continue;
          }

          const albumId = album.uri.split('/').pop();
          if (!albumId) {
            console.warn(`Album ID not found for album: ${album.name}`);
            continue;
          }
          const videosResponse = await fetch(
            `https://api.vimeo.com/albums/${albumId}/videos?per_page=${numVideos}&sort=date&direction=desc&fields=uri,name,description,created_time,pictures.sizes.width,pictures.sizes.link`,
            { headers }
          );

          if (!videosResponse.ok) {
            throw new Error(`Videos API error for album ${albumId}: ${videosResponse.status}`);
          }

          const videosData: VimeoVideo.Response = await videosResponse.json();

          // Double-check that we actually got videos (in case metadata was incorrect)
          if (videosData.data && videosData.data.length > 0) {
            videosDataAgg[albumId] = videosData;
            albumsDataAgg.data.push(album);
            albumsWithVideos++;
          }
        }

        // If we still need more albums and there are more pages, recursively fetch the next page
        if (albumsWithVideos < numAlbums && albumsData.paging.next) {
          return fetchAlbumsWithVideos(page + 1, albumsWithVideos);
        }

        return albumsWithVideos;
      };

      // Start the recursive fetching process
      const totalAlbumsWithVideos = await fetchAlbumsWithVideos();

      // Update total count in the response
      albumsDataAgg.total = albumsDataAgg.data.length;

      // If we couldn't find enough albums with videos, log a message
      if (totalAlbumsWithVideos < numAlbums) {
        console.warn(`Could only find ${totalAlbumsWithVideos} albums with videos out of ${numAlbums} requested.`);
      }

      // Clear all existing mocks before transforming and creating new ones
      clearAllMocks();

      // Transform Vimeo data into RESI mock data format
      transformAndCreateMocks({
        userData,
        albumsData: albumsDataAgg,
        videosData: videosDataAgg,
      });

      const successMessage = `Vimeo data successfully fetched and transformed into mocks. All existing mocks were cleared.`;
      setSuccess(successMessage);
      triggerRefresh();
      resetPendingChanges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const transformAndCreateMocks = (vimeoData: VimeoData) => {
    if (!vimeoData.userData || !vimeoData.albumsData.data.length) {
      return;
    }

    const userData = vimeoData.userData;
    const userName = userData.name;
    const userDescription = userData.bio || '';

    // Create feed mock (site)
    const feedAlbums = vimeoData.albumsData.data.map((album) => {
      const albumId = album.uri.split('/').pop();
      return {
        id: albumId ?? '',
        name: album.name,
        size: album.metadata.connections.videos.total || 0,
        url: `/api/playlists/${albumId}`,
      };
    });

    const id = `site-vimeo-${userId}`;

    addSiteMock(
      {
        id,
        description: `Vimeo Channel: ${userName}`,
        endpoint: '*sites-boot.resi.io/api/v1/sites?url=*',
        data: {
          id,
          customerId: `customer-vimeo-${userId}`,
          channelId: 'preview-channel-id',
          name: `${userName} - Vimeo Channel`,
          description: userDescription || 'Vimeo Channel Content',
          url: userData.link,
          branding: {
            mode: 'dark',
          },
          feeds: [
            {
              id: `feed-vimeo-${userId}`,
              name: 'Vimeo Feed',
              description: 'Content from Vimeo channel',
              default: true,
              playlists: feedAlbums,
            },
          ],
        },
      },
      true
    );

    // Create playlist mocks for each album
    vimeoData.albumsData.data.forEach((album) => {
      const albumId = album.uri.split('/').pop() ?? '';
      const albumVideos = vimeoData.videosData[albumId];

      if (!albumVideos.data) {
        return;
      }

      const videos = albumVideos.data.map((video) => {
        const videoId = video.uri.split('/').pop() ?? '';
        const largestThumbnail = video.pictures.sizes.reduce((largest, current) =>
          current.width > largest.width ? current : largest
        );
        return {
          id: videoId,
          title: video.name,
          description: video.description || '',
          thumbnail: {
            url: largestThumbnail.link || 'https://placehold.co/300x200?text=Vimeo+Video',
          },
          airDate: video.created_time,
          embedId,
          formattedDescription: `**${video.description || ''}**`,
        };
      });

      addPlaylistMock(
        {
          id: albumId,
          description: `Vimeo Album: ${album.name}`,
          endpoint: `*/api/playlists/${albumId}*`,
          data: {
            id: albumId,
            name: album.name,
            videos: videos,
          },
        },
        true
      );

      // Create individual video mocks
      videos.forEach((video) => {
        addVideoMock(
          {
            id: video.id,
            description: `Vimeo Video: ${video.title}`,
            endpoint: `*/ondemand/videos/${video.id}`,
            data: {
              id: video.id,
              title: video.title,
              description: video.description,
              thumbnail: {
                url: video.thumbnail.url,
              },
              airDate: video.airDate,
              embedId,
              tags: ['vimeo'],
              playlists: [{ id: albumId, url: `/api/playlists/${albumId}` }],
            },
          },
          true
        );
      });
    });
  };

  return (
    <Draft.Card>
      <Draft.CardSection>
        <Stack gap="m">
          <Text variant="h3">Vimeo Data Fetcher</Text>
          <Text>Fetch data from a Vimeo channel and transform it into mock data</Text>

          <Stack gap="s">
            <Draft.FormField fieldLabel="User ID">
              <Draft.TextInput
                css={css`
                  width: 100%;
                `}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter Vimeo user ID"
                value={userId}
              />
            </Draft.FormField>

            <Box
              css={css`
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
              `}
            >
              <Draft.FormField fieldLabel="Max Albums">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setNumAlbums(parseInt(e.target.value, 10) || 5)}
                  type="number"
                  value={numAlbums.toString()}
                />
              </Draft.FormField>

              <Draft.FormField fieldLabel="Max Videos per Album">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setNumVideos(parseInt(e.target.value, 10) || 10)}
                  type="number"
                  value={numVideos.toString()}
                />
              </Draft.FormField>
            </Box>

            {error && <Text color="error">{error}</Text>}

            {success && <Text color="success">{success}</Text>}

            <Draft.Button
              css={css`
                margin-top: 8px;
              `}
              disabled={isLoading}
              label="Fetch Vimeo Data"
              onClick={fetchVimeoData}
            >
              {isLoading ? 'Loading...' : 'Fetch Vimeo Data'}
            </Draft.Button>
          </Stack>
        </Stack>
      </Draft.CardSection>
    </Draft.Card>
  );
};

export default VimeoDataFetcherCard;
